import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import subscriptionApi, {
  SubscriptionDetails,
  SubscriptionTier,
  EstimateResult,
  TierPreview,
} from '../services/subscriptionApi';
import apiService from '../services/api';
import { usePaymentAttempt } from '../services/idempotency';
import AssignBillingProfileModal from '../components/AssignBillingProfileModal';
import { useToast } from '../context/ToastContext';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CreditCard,
  Lock,
  Tag,
  Calendar,
  RefreshCw,
  CircleCheck,
  Loader2,
  AlertTriangle,
  Info,
  Sparkles,
} from 'lucide-react';

interface SelectedAddon {
  addon_code: string;
  quantity: number;
}

interface CheckoutState {
  tier: SubscriptionTier;
  billingCycle: 'monthly' | 'annual';
  addons: SelectedAddon[];
  estimate: EstimateResult;
}

const CheckoutPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const workspaceId = parseInt(id || '0', 10);
const { showSuccess, showError } = useToast();
  const payAttempt = usePaymentAttempt();

  const checkoutState = location.state as CheckoutState | null;

  const [workspace, setWorkspace] = useState<any>(null);
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(false);

  // Promo code
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoValidation, setPromoValidation] = useState<{
    valid: boolean;
    discount?: string;
    message?: string;
  } | null>(null);
  const [promoFocused, setPromoFocused] = useState(false);

  const [currentEstimate, setCurrentEstimate] = useState<EstimateResult | null>(checkoutState?.estimate || null);
  const [tierPreview, setTierPreview] = useState<TierPreview | null>(null);
  const [assignBillingModalOpen, setAssignBillingModalOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);

  // Hover states
  const [backHovered, setBackHovered] = useState(false);
  const [applyHovered, setApplyHovered] = useState(false);
  const [changeHovered, setChangeHovered] = useState(false);
  const [confirmHovered, setConfirmHovered] = useState(false);
  const [doneHovered, setDoneHovered] = useState(false);

  useEffect(() => {
    if (!checkoutState) {
      navigate(`/dashboard/workspace/${workspaceId}/subscription`);
      return;
    }
    loadData();
  }, [workspaceId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [wsResponse, subData] = await Promise.all([
        apiService.get(`/workspaces/${workspaceId}`),
        subscriptionApi.getSubscription(workspaceId),
      ]);
      setWorkspace(wsResponse.data);
      setSubscription(subData);

      if (checkoutState) {
        try {
          const freshEstimate = await subscriptionApi.getEstimate(workspaceId, {
            tier_id: checkoutState.tier.id,
            billing_cycle: checkoutState.billingCycle,
            addons: checkoutState.addons,
          });
          setCurrentEstimate(freshEstimate);
        } catch (err) {
          console.error('Failed to refresh estimate:', err);
        }

        if (subData.is_lifetime) {
          try {
            const preview = await subscriptionApi.previewTierLicense(workspaceId, checkoutState.tier.id);
            setTierPreview(preview);
          } catch (err) {
            console.error('Failed to fetch tier preview:', err);
          }
        }
      }
    } catch (err: any) {
      showError('Failed to load subscription details');
    } finally {
      setLoading(false);
    }
  };

  const handleValidatePromo = async () => {
    if (!promoCode.trim() || !checkoutState) return;

    setPromoLoading(true);
    setPromoValidation(null);

    try {
      const result = await subscriptionApi.validatePromoCode(promoCode, checkoutState.tier.id);

      if (result.valid) {
        const newEstimate = await subscriptionApi.getEstimate(workspaceId, {
          tier_id: checkoutState.tier.id,
          billing_cycle: checkoutState.billingCycle,
          addons: checkoutState.addons,
          promo_code: promoCode,
        });
        setCurrentEstimate(newEstimate);
        let discountDescription = 'Discount';
        if (result.coupon) {
          if (result.coupon.discount_percentage) {
            discountDescription = `${result.coupon.discount_percentage}% off`;
          } else if (result.coupon.discount_amount) {
            discountDescription = `$${(result.coupon.discount_amount / 100).toFixed(2)} off`;
          }
        }
        setPromoValidation({
          valid: true,
          discount: discountDescription,
          message: `${discountDescription} applied!`,
        });
      } else {
        setPromoValidation({
          valid: false,
          message: result.error || 'Invalid promo code',
        });
      }
    } catch (err: any) {
      setPromoValidation({
        valid: false,
        message: err.message || 'Failed to validate promo code',
      });
    } finally {
      setPromoLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!checkoutState || !currentEstimate) return;

    setProcessing(true);

try {
      const isTrial = subscription?.status === 'trial' || subscription?.status === 'trial_expired' || subscription?.status === 'transfer_trial';
      const isLifetime = subscription?.status === 'lifetime';

      // 3DS: authorize the tax-inclusive amount due on-session for EEA/Stripe cards
      // before charging. Authnet/US (or nothing due) returns gateway:'none' and
      // falls through to off-session collection. One PI covers convert/create/change.
      const ensureStripe = (pk: string): Promise<any> => new Promise((resolve, reject) => {
        const w = window as any;
        if (w.Stripe) { resolve(w.Stripe(pk)); return; }
        const existing = document.querySelector('script[src="https://js.stripe.com/v3/"]');
        const onload = () => resolve((window as any).Stripe(pk));
        if (existing) { existing.addEventListener('load', onload); return; }
        const s = document.createElement('script');
        s.src = 'https://js.stripe.com/v3/';
        s.onload = onload;
        s.onerror = () => reject(new Error('Failed to load Stripe.js'));
        document.head.appendChild(s);
      });

// One idempotency key per attempt, paired with its PI. Both are reused across
      // an ambiguous (network/5xx) retry so Chargebee replays instead of charging
      // twice, and both reset on a definitive decline or success.
      const idempotency_key = payAttempt.beginKey();
      let paymentIntentId: string | undefined = payAttempt.getPI();
      if (!paymentIntentId) {
        const intent = await subscriptionApi.createSubscriptionPaymentIntent(workspaceId, {
          tier_id: checkoutState.tier.id,
          billing_cycle: checkoutState.billingCycle,
          promo_code: promoValidation?.valid ? promoCode : undefined,
          addons: checkoutState.addons,
        });
        if (intent && intent.gateway === 'stripe') {
          paymentIntentId = intent.paymentIntentId;
          if (intent.requiresAction) {
            const stripe = await ensureStripe(intent.stripePublishableKey);
            const result = await stripe.confirmCardPayment(intent.clientSecret);
            if (result.error) {
              throw new Error(result.error.message || 'Card authentication failed');
            }
          }
        }
        payAttempt.setPI(paymentIntentId);
      }

      if (isTrial) {
        await subscriptionApi.convertTrial(workspaceId, {
          tier_id: checkoutState.tier.id,
          billing_cycle: checkoutState.billingCycle,
          promo_code: promoValidation?.valid ? promoCode : undefined,
          addons: checkoutState.addons,
          payment_intent_id: paymentIntentId,
          idempotency_key,
        });
      } else if (isLifetime || subscription?.status === 'pending_deletion' || subscription?.status === 'none') {
        // No live subscription (lifetime, pending deletion, never subscribed): create a new one.
        await subscriptionApi.createSubscription(workspaceId, {
          tier_id: checkoutState.tier.id,
          billing_cycle: checkoutState.billingCycle,
          promo_code: promoValidation?.valid ? promoCode : undefined,
          addons: checkoutState.addons,
          payment_intent_id: paymentIntentId,
          idempotency_key,
        });
      } else {
        await subscriptionApi.changeTier(workspaceId, checkoutState.tier.id, {
          billing_cycle: checkoutState.billingCycle,
          immediate: true,
          addons: checkoutState.addons,
          promo_code: promoValidation?.valid ? promoCode : undefined,
          payment_intent_id: paymentIntentId,
          idempotency_key,
        });
      }
      payAttempt.succeed();
      showSuccess(isTrial ? 'Subscription activated!' : 'Plan updated successfully');
      setSuccess(true);
    } catch (err: any) {
      payAttempt.fail(err);
      showError(err.message || 'Failed to process subscription change');
    } finally {
      setProcessing(false);
    }
  };

  const handleBack = () => {
navigate(`/dashboard/workspace/${workspaceId}/subscription`, { state: { tabIndex: 2 } });
  };

  const handleDone = () => {
    navigate(`/dashboard/workspace/${workspaceId}/subscription`, { state: { refresh: Date.now() } });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', backgroundColor: 'var(--v2-bg)' }}>
        <div
          style={{
            width: '32px',
            height: '32px',
            border: '2px solid var(--v2-border)',
            borderTopColor: 'var(--v2-primary)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      </div>
    );
  }

  if (!checkoutState) {
    return null;
  }

const isTrial = subscription?.status === 'trial' || subscription?.status === 'trial_expired' || subscription?.status === 'transfer_trial';
  const currentTier = subscription?.tier;
  const newTier = checkoutState.tier;
  const hasPaymentMethod = subscription?.has_payment_method;

  const getCurrentPrice = () => {
    if (!currentTier || isTrial) return null;
    const basePrice =
      subscription?.billing_cycle === 'annual' ? parseFloat(currentTier.price_annual) : parseFloat(currentTier.price_monthly);
    const addonTotal = (subscription?.addons || []).reduce((sum, a) => sum + parseFloat(a.price || '0') * (a.quantity || 1), 0);
    return basePrice + addonTotal;
  };

  const getRecurringTotal = () => {
    if (!currentEstimate) return 0;
return currentEstimate.recurring_total_with_tax ?? currentEstimate.recurring_total ?? currentEstimate.total;
  };

  // ============================================
  // SUCCESS STATE
  // ============================================
  if (success) {
    return (
      <div style={{ padding: '32px 40px', maxWidth: '600px', margin: '0 auto', fontFamily: 'var(--font-sans)' }}>
        <div
          style={{
            backgroundColor: 'var(--v2-surface)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--v2-border)',
            padding: '60px 40px',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              width: '72px',
              height: '72px',
              borderRadius: '50%',
              backgroundColor: 'var(--v2-success-bg)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}
          >
            <CircleCheck size={36} strokeWidth={1.5} color="var(--v2-success-dark)" />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 12px 0' }}>
            {isTrial ? 'Welcome Aboard!' : 'Plan Updated!'}
          </h1>
          <p style={{ fontSize: '0.9375rem', color: 'var(--v2-text-secondary)', margin: '0 0 8px 0' }}>
            Your workspace <strong style={{ fontWeight: 600 }}>{workspace?.name}</strong> is now on the{' '}
            <strong style={{ fontWeight: 600 }}>{newTier.tier_name}</strong> plan.
          </p>
          <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: '0 0 28px 0' }}>
            {checkoutState.billingCycle === 'annual'
              ? `You'll be billed $${getRecurringTotal().toFixed(2)} annually.`
              : `You'll be billed $${getRecurringTotal().toFixed(2)} monthly.`}
          </p>
          <PrimaryButton onClick={handleDone} hovered={doneHovered} setHovered={setDoneHovered} large>
            Go to Subscription
            <ArrowRight size={18} strokeWidth={1.5} />
          </PrimaryButton>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN CHECKOUT VIEW
  // ============================================
  return (
    <div style={{ padding: '32px 40px', maxWidth: '800px', margin: '0 auto', fontFamily: 'var(--font-sans)', backgroundColor: 'var(--v2-bg)', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <button
          onClick={handleBack}
          onMouseEnter={() => setBackHovered(true)}
          onMouseLeave={() => setBackHovered(false)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 0',
            marginBottom: '16px',
            backgroundColor: 'transparent',
            color: backHovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
            border: 'none',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'color 0.15s ease',
          }}
        >
          <ArrowLeft size={16} strokeWidth={1.5} />
          Back to Plan Selection
        </button>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 4px 0', letterSpacing: '-0.01em' }}>
          Review Your {isTrial ? 'Subscription' : 'Plan Changes'}
        </h1>
        <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>{workspace?.name}</p>
      </div>

      {/* Plan Comparison */}
      <Card>
        <CardHeader title={isTrial ? 'Your New Plan' : 'Plan Comparison'} icon={<Sparkles size={16} strokeWidth={1.5} />} />
        <CardBody>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', alignItems: 'stretch', flexWrap: 'wrap' }}>
            {/* Current Plan (only show if not trial) */}
            {!isTrial && currentTier && (
              <>
                <PlanCard
                  label="Current Plan"
                  tierName={currentTier.tier_name}
                  price={getCurrentPrice() || 0}
                  billingCycle={subscription?.billing_cycle || 'monthly'}
                  namedUsers={
                    subscription?.is_lifetime ? subscription.effective_license?.named_users : currentTier.base_named_users
                  }
                  publicViewUsers={
                    subscription?.is_lifetime
                      ? subscription.effective_license?.public_view_users
                      : currentTier.base_public_view_users
                  }
                  addons={subscription?.addons}
                  isCurrent
                />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 8px' }}>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--v2-primary-light)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <ArrowRight size={20} strokeWidth={1.5} color="var(--v2-primary)" />
                  </div>
                </div>
              </>
            )}

            {/* New Plan */}
            <PlanCard
              label="New Plan"
              tierName={newTier.tier_name}
              price={getRecurringTotal()}
              billingCycle={checkoutState.billingCycle}
              namedUsers={tierPreview ? tierPreview.named_users : newTier.base_named_users}
              publicViewUsers={tierPreview ? tierPreview.public_view_users : newTier.base_public_view_users}
              lifetimeBonus={tierPreview?.lifetime_bonus}
              recurringAddons={currentEstimate?.recurring_line_items?.filter((item) => item.type === 'addon')}
              isNew
            />
          </div>
        </CardBody>
      </Card>

      {/* Charges Today */}
      <Card>
        <CardHeader title={isTrial ? 'Payment Summary' : 'Charges Today'} icon={<CreditCard size={16} strokeWidth={1.5} />} />
        <CardBody>
          {currentEstimate ? (
            <div>
              {(currentEstimate as any).no_changes ? (
                <Alert type="info" message="No changes to your current plan. Your subscription will continue as-is." />
              ) : (
                <>
                  {/* Charges */}
                  {currentEstimate.line_items
                    .filter((item) => item.amount > 0)
                    .map((item, idx) => (
                      <LineItem key={`charge-${idx}`} label={`${item.description}${item.quantity > 1 ? ` (×${item.quantity})` : ''}`} amount={item.amount} />
                    ))}

                  {/* Credits */}
                  {currentEstimate.line_items
                    .filter((item) => item.amount < 0)
                    .map((item, idx) => (
                      <LineItem
                        key={`credit-${idx}`}
                        label={item.description.includes('Credit') ? item.description : `${item.description} (credit)`}
                        amount={item.amount}
                        isCredit
                      />
                    ))}

                  {currentEstimate.credits_applied > 0 && (
                    <LineItem label="Account credits applied" amount={-currentEstimate.credits_applied} isCredit />
                  )}

                  {currentEstimate.discounts.map((discount, idx) => (
                    <LineItem key={idx} label={discount.description} amount={-Math.abs(discount.amount)} isCredit />
                  ))}

{promoValidation?.valid && (
                    <LineItem label={`Promo: ${promoCode}`} amount={0} note={promoValidation.discount} isCredit />
                  )}

                  {(currentEstimate.taxes || []).map((t, idx) => (
                    <LineItem key={`tax-${idx}`} label={t.description || t.name || 'Tax'} amount={t.amount} />
                  ))}
                </>
              )}

              {/* Total */}
              <div
                style={{
                  borderTop: '1px solid var(--v2-border)',
                  marginTop: '16px',
                  paddingTop: '16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--v2-text)' }}>Due Today</span>
                <span style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--v2-primary)' }}>
                  ${currentEstimate.amount_due.toFixed(2)}
                </span>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
              <Loader2 size={24} strokeWidth={1.5} color="var(--v2-primary)" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          )}
        </CardBody>
      </Card>

      {/* Going Forward */}
      <Card>
        <CardHeader title="Going Forward" icon={<Calendar size={16} strokeWidth={1.5} />} />
        <CardBody>
          {currentEstimate?.recurring_line_items && (
            <div style={{ marginBottom: '16px' }}>
              {currentEstimate.recurring_line_items.map((item, idx) => (
                <LineItem key={idx} label={`${item.description}${item.quantity > 1 ? ` (×${item.quantity})` : ''}`} amount={item.amount} />
              ))}
              {(currentEstimate as any).recurring_discount && (
                <LineItem
                  label={`${(currentEstimate as any).recurring_discount.description}${(currentEstimate as any).recurring_discount.percentage ? ` (${(currentEstimate as any).recurring_discount.percentage}% off)` : ''}`}
                  amount={-(currentEstimate as any).recurring_discount.amount}
                  isCredit
                />
              )}
              <div style={{ borderTop: '1px solid var(--v2-border)', marginTop: '12px' }} />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <RefreshCw size={16} strokeWidth={1.5} color="var(--v2-text-muted)" />
            <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
              Starting{' '}
              <strong style={{ fontWeight: 600, color: 'var(--v2-text)' }}>
                {currentEstimate?.next_billing_at
                  ? new Date(currentEstimate.next_billing_at).toLocaleDateString()
                  : currentEstimate?.current_period_end
                    ? new Date(currentEstimate.current_period_end).toLocaleDateString()
                    : 'next billing cycle'}
              </strong>
              , you'll be billed:
            </span>
          </div>
          <p style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--v2-primary)', margin: '8px 0 0 0' }}>
            ${getRecurringTotal().toFixed(2)}
            <span style={{ fontSize: '0.9375rem', fontWeight: 500, color: 'var(--v2-text-tertiary)', marginLeft: '4px' }}>
              /{checkoutState.billingCycle === 'annual' ? 'year' : 'month'}
            </span>
          </p>
        </CardBody>
      </Card>

      {/* Promo Code */}
      <Card>
        <CardHeader title="Promo Code" icon={<Tag size={16} strokeWidth={1.5} />} />
        <CardBody>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
            <input
              type="text"
              placeholder="Enter promo code"
              value={promoCode}
              onChange={(e) => {
                setPromoCode(e.target.value.toUpperCase());
                setPromoValidation(null);
              }}
              onFocus={() => setPromoFocused(true)}
              onBlur={() => setPromoFocused(false)}
              disabled={promoLoading || processing}
              style={{
                padding: '10px 14px',
                backgroundColor: 'var(--v2-surface)',
                border: `1px solid ${promoFocused ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.875rem',
                fontFamily: 'var(--font-mono)',
                color: 'var(--v2-text)',
                width: '200px',
                outline: 'none',
                transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
                boxShadow: promoFocused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
              }}
            />
            <SecondaryButton
              onClick={handleValidatePromo}
              disabled={promoLoading || !promoCode.trim() || processing}
              hovered={applyHovered}
              setHovered={setApplyHovered}
            >
              {promoLoading ? 'Applying...' : 'Apply'}
            </SecondaryButton>
          </div>
          {promoValidation && (
            <div style={{ marginTop: '12px' }}>
              <Alert type={promoValidation.valid ? 'success' : 'error'} message={promoValidation.message || ''} />
            </div>
          )}
        </CardBody>
      </Card>

      {/* Payment Method */}
      <Card>
        <CardHeader title="Payment Method" icon={<CreditCard size={16} strokeWidth={1.5} />} />
        <CardBody>
          {hasPaymentMethod ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                <div
                  style={{
                    width: '44px',
                    height: '44px',
                    borderRadius: 'var(--radius-md)',
                    backgroundColor: 'var(--v2-primary-light)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CreditCard size={20} strokeWidth={1.5} color="var(--v2-primary)" />
                </div>
                <div>
                  <p style={{ fontSize: '0.9375rem', fontWeight: 500, color: 'var(--v2-text)', margin: '0 0 4px 0' }}>
                    {subscription?.billing_profile?.name || 'Payment method on file'}
                  </p>
                  <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
                    Will be charged ${currentEstimate?.amount_due.toFixed(2) || '0.00'}
                  </p>
                </div>
              </div>
              <GhostButton onClick={() => setAssignBillingModalOpen(true)} hovered={changeHovered} setHovered={setChangeHovered}>
                Change
              </GhostButton>
            </div>
          ) : (
            <Alert
              type="warning"
              message="You need to add a payment method before subscribing."
              action={
                <SecondaryButton onClick={() => navigate('/dashboard/billing')} hovered={false} setHovered={() => {}}>
                  Add Payment Method
                </SecondaryButton>
              }
            />
          )}
        </CardBody>
      </Card>

      {/* Subscription Agreement */}
      <Card noPadding>
        <div style={{ padding: '20px 24px' }}>
          <div
            style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', cursor: 'pointer' }}
            onClick={() => setConsentChecked(!consentChecked)}
          >
            <div
              style={{
                width: '22px',
                height: '22px',
                border: `2px solid ${consentChecked ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: consentChecked ? 'var(--v2-primary)' : 'transparent',
                flexShrink: 0,
                marginTop: '2px',
                transition: 'all 0.15s ease',
              }}
            >
              {consentChecked && <Check size={14} strokeWidth={3} color="#ffffff" />}
            </div>
            <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-secondary)', lineHeight: 1.7, margin: 0 }}>
              I agree to subscribe. My payment method will be charged{' '}
              <strong style={{ fontWeight: 600 }}>${currentEstimate?.amount_due.toFixed(2) || '0.00'}</strong> now and my subscription
              will <strong style={{ fontWeight: 600 }}>automatically renew</strong> at{' '}
              <strong style={{ fontWeight: 600 }}>
                ${getRecurringTotal().toFixed(2)}/{checkoutState.billingCycle === 'annual' ? 'year' : 'month'}
              </strong>{' '}
              (at then-current rates) until I cancel. I can cancel anytime before my next renewal to avoid future charges. I agree to
              the{' '}
              <a
href="https://dashboardfox.com/legal/terms"
                target="_blank"
                style={{ color: 'var(--v2-primary)', textDecoration: 'none', fontWeight: 500 }}
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
href="https://dashboardfox.com/legal/privacy"
                target="_blank"
                style={{ color: 'var(--v2-primary)', textDecoration: 'none', fontWeight: 500 }}
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={(e) => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseLeave={(e) => (e.currentTarget.style.textDecoration = 'none')}
              >
                Privacy Policy
              </a>
              .
            </p>
          </div>
        </div>
      </Card>

      {/* Confirm Button */}
      <div style={{ marginTop: '28px' }}>
        <button
          onClick={handleConfirm}
          disabled={processing || !hasPaymentMethod || !currentEstimate || !consentChecked}
          onMouseEnter={() => setConfirmHovered(true)}
          onMouseLeave={() => setConfirmHovered(false)}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: '100%',
            padding: '16px 24px',
            backgroundColor:
              processing || !hasPaymentMethod || !currentEstimate || !consentChecked
                ? 'var(--v2-border)'
                : confirmHovered
                  ? 'var(--v2-primary-hover)'
                  : 'var(--v2-primary)',
            color: processing || !hasPaymentMethod || !currentEstimate || !consentChecked ? 'var(--v2-text-muted)' : '#ffffff',
            border: 'none',
            borderRadius: 'var(--radius-md)',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: processing || !hasPaymentMethod || !currentEstimate || !consentChecked ? 'not-allowed' : 'pointer',
            transition: 'background-color 0.15s ease',
          }}
        >
          {processing ? (
            <>
              <Loader2 size={20} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
              Processing...
            </>
          ) : (
            <>
              <Lock size={18} strokeWidth={2} />
              Confirm and Pay ${currentEstimate?.amount_due.toFixed(2) || '0.00'}
            </>
          )}
        </button>
      </div>

      {/* Subscription Terms Footer */}
      <div style={{ marginTop: '32px', paddingTop: '24px', borderTop: '1px solid var(--v2-border)' }}>
        <p style={{ fontSize: '0.6875rem', color: 'var(--v2-text-muted)', lineHeight: 1.8, margin: 0 }}>
          <strong style={{ fontWeight: 600 }}>SUBSCRIPTION DETAILS:</strong> The subscription plan you choose will automatically renew
          and you will be charged at then-current rates for that plan until you cancel. There are no refunds except as provided in our
          Terms of Service. Cancel at least 24 hours prior to renewal; cancellation is effective at the end of your current billing
          period. Please see our{' '}
<a href="https://dashboardfox.com/legal/terms" target="_blank" style={{ color: 'var(--v2-text-tertiary)' }}>
            Terms of Service
          </a>{' '}
          and{' '}
<a href="https://dashboardfox.com/legal/privacy" target="_blank" style={{ color: 'var(--v2-text-tertiary)' }}>
            Privacy Policy
          </a>{' '}
          for complete terms, our refund policy, and how to cancel. All fees, content, and features are subject to change.
        </p>
      </div>

      {/* Billing Profile Modal */}
      <AssignBillingProfileModal
        open={assignBillingModalOpen}
        onClose={() => setAssignBillingModalOpen(false)}
        workspaceId={workspaceId}
        workspaceName={workspace?.name || ''}
        currentBillingProfileId={subscription?.billing_profile?.id || null}
        onAssigned={() => {
          setAssignBillingModalOpen(false);
          loadData();
        }}
      />

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

// ============================================
// REUSABLE COMPONENTS
// ============================================

// Card
const Card: React.FC<{ children: React.ReactNode; noPadding?: boolean }> = ({ children, noPadding }) => (
  <div
    style={{
      backgroundColor: 'var(--v2-surface)',
      borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--v2-border)',
      overflow: 'hidden',
      marginBottom: '16px',
    }}
  >
    {children}
  </div>
);

// Card Header
const CardHeader: React.FC<{ title: string; icon?: React.ReactNode }> = ({ title, icon }) => (
  <div
    style={{
      padding: '14px 20px',
      backgroundColor: 'var(--v2-surface-alt)',
      borderBottom: '1px solid var(--v2-border)',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    }}
  >
    {icon && <span style={{ color: 'var(--v2-text-muted)' }}>{icon}</span>}
    <h2 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)', margin: 0 }}>{title}</h2>
  </div>
);

// Card Body
const CardBody: React.FC<{ children: React.ReactNode }> = ({ children }) => <div style={{ padding: '20px' }}>{children}</div>;

// Plan Card
const PlanCard: React.FC<{
  label: string;
  tierName: string;
  price: number;
  billingCycle: string;
  namedUsers: number;
  publicViewUsers: number;
  addons?: any[];
  recurringAddons?: any[];
  lifetimeBonus?: { named_users?: number; public_view_users?: number };
  isCurrent?: boolean;
  isNew?: boolean;
}> = ({ label, tierName, price, billingCycle, namedUsers, publicViewUsers, addons, recurringAddons, lifetimeBonus, isCurrent, isNew }) => (
  <div
    style={{
      flex: '1 1 280px',
      maxWidth: '300px',
      backgroundColor: isNew ? 'var(--v2-surface)' : 'var(--v2-surface-alt)',
      borderRadius: 'var(--radius-md)',
      border: isNew ? '2px solid var(--v2-primary)' : '1px solid var(--v2-border)',
      padding: '20px',
    }}
  >
    <p
      style={{
        fontSize: '0.6875rem',
        fontWeight: 600,
        color: isNew ? 'var(--v2-primary)' : 'var(--v2-text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        margin: '0 0 4px 0',
      }}
    >
      {label}
    </p>
    <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 12px 0' }}>{tierName}</h3>
    <p style={{ fontSize: '1.25rem', fontWeight: 700, color: isNew ? 'var(--v2-primary)' : 'var(--v2-text-secondary)', margin: '0 0 8px 0' }}>
      ${price.toFixed(2)}
      <span style={{ fontSize: '0.8125rem', fontWeight: 400, color: 'var(--v2-text-tertiary)', marginLeft: '2px' }}>
        /{billingCycle === 'annual' ? 'year' : 'month'}
      </span>
    </p>
    {isNew && (
      <span
        style={{
          display: 'inline-block',
          padding: '4px 10px',
          backgroundColor: 'var(--v2-primary-light)',
          color: 'var(--v2-primary)',
          borderRadius: 'var(--radius-full)',
          fontSize: '0.6875rem',
          fontWeight: 600,
          marginBottom: '12px',
        }}
      >
        {billingCycle === 'annual' ? 'Annual Billing' : 'Monthly Billing'}
      </span>
    )}
    <div style={{ borderTop: '1px solid var(--v2-border)', paddingTop: '12px' }}>
      <p style={{ fontSize: '0.8125rem', color: isNew ? 'var(--v2-text)' : 'var(--v2-text-secondary)', margin: '0 0 4px 0' }}>
        {namedUsers} Named Users
        {lifetimeBonus?.named_users && (
          <span style={{ color: 'var(--v2-success)', fontWeight: 600, marginLeft: '4px', fontSize: '0.75rem' }}>
            (+{lifetimeBonus.named_users} lifetime)
          </span>
        )}
      </p>
      <p style={{ fontSize: '0.8125rem', color: isNew ? 'var(--v2-text)' : 'var(--v2-text-secondary)', margin: '0 0 4px 0' }}>
        {publicViewUsers} Public View Users
        {lifetimeBonus?.public_view_users && (
          <span style={{ color: 'var(--v2-success)', fontWeight: 600, marginLeft: '4px', fontSize: '0.75rem' }}>
            (+{lifetimeBonus.public_view_users} lifetime)
          </span>
        )}
      </p>
      {addons?.map((addon) => (
        <p key={addon.addon_code} style={{ fontSize: '0.8125rem', color: 'var(--v2-text-secondary)', margin: '0 0 4px 0' }}>
          + {addon.addon_name} {addon.quantity > 1 && `×${addon.quantity}`}
        </p>
      ))}
      {recurringAddons?.map((item, idx) => (
        <p key={idx} style={{ fontSize: '0.8125rem', color: 'var(--v2-primary)', fontWeight: 500, margin: '0 0 4px 0' }}>
          + {item.description} {item.quantity > 1 && `×${item.quantity}`}
        </p>
      ))}
    </div>
  </div>
);

// Line Item
const LineItem: React.FC<{ label: string; amount: number; isCredit?: boolean; note?: string }> = ({ label, amount, isCredit, note }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
    <span style={{ fontSize: '0.875rem', color: isCredit ? 'var(--v2-success-dark)' : 'var(--v2-text-secondary)', fontWeight: isCredit ? 500 : 400 }}>
      {label}
    </span>
    <span style={{ fontSize: '0.875rem', color: isCredit ? 'var(--v2-success-dark)' : 'var(--v2-text)', fontWeight: 500 }}>
      {note || (isCredit && amount !== 0 ? `-$${Math.abs(amount).toFixed(2)}` : `$${amount.toFixed(2)}`)}
    </span>
  </div>
);

// Alert
const Alert: React.FC<{ type: 'success' | 'error' | 'warning' | 'info'; message: string; action?: React.ReactNode }> = ({
  type,
  message,
  action,
}) => {
  const config = {
    success: { bg: 'var(--v2-success-bg)', border: 'var(--v2-success)', icon: CircleCheck, color: '#166534' },
    error: { bg: 'var(--v2-error-bg)', border: 'var(--v2-error)', icon: AlertTriangle, color: '#991b1b' },
    warning: { bg: 'var(--v2-warning-bg)', border: '#fcd34d', icon: AlertTriangle, color: '#92400e' },
    info: { bg: 'var(--v2-info-bg)', border: 'var(--v2-primary)', icon: Info, color: 'var(--v2-primary)' },
  };
  const c = config[type];
  const Icon = c.icon;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '14px 16px',
        backgroundColor: c.bg,
        borderLeft: `4px solid ${c.border}`,
        borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
      }}
    >
      <Icon size={18} strokeWidth={1.5} color={c.color} style={{ flexShrink: 0, marginTop: '1px' }} />
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: '0.875rem', color: c.color, margin: 0 }}>{message}</p>
        {action && <div style={{ marginTop: '12px' }}>{action}</div>}
      </div>
    </div>
  );
};

// Primary Button
const PrimaryButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  hovered: boolean;
  setHovered: (h: boolean) => void;
  disabled?: boolean;
  large?: boolean;
}> = ({ children, onClick, hovered, setHovered, disabled, large }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    onMouseEnter={() => setHovered(true)}
    onMouseLeave={() => setHovered(false)}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: large ? '14px 28px' : '10px 20px',
      backgroundColor: disabled ? 'var(--v2-border)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
      color: disabled ? 'var(--v2-text-muted)' : '#ffffff',
      border: 'none',
      borderRadius: 'var(--radius-md)',
      fontSize: large ? '1rem' : '0.875rem',
      fontWeight: 500,
      cursor: disabled ? 'not-allowed' : 'pointer',
      transition: 'background-color 0.15s ease',
    }}
  >
    {children}
  </button>
);

// Secondary Button
const SecondaryButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  hovered: boolean;
  setHovered: (h: boolean) => void;
  disabled?: boolean;
}> = ({ children, onClick, hovered, setHovered, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    onMouseEnter={() => setHovered(true)}
    onMouseLeave={() => setHovered(false)}
    style={{
      padding: '10px 16px',
      backgroundColor: hovered && !disabled ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
      color: disabled ? 'var(--v2-text-muted)' : 'var(--v2-text)',
      border: '1px solid var(--v2-border)',
      borderRadius: 'var(--radius-sm)',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      transition: 'all 0.15s ease',
    }}
  >
    {children}
  </button>
);

// Ghost Button
const GhostButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  hovered: boolean;
  setHovered: (h: boolean) => void;
}> = ({ children, onClick, hovered, setHovered }) => (
  <button
    onClick={onClick}
    onMouseEnter={() => setHovered(true)}
    onMouseLeave={() => setHovered(false)}
    style={{
      padding: '8px 12px',
      backgroundColor: 'transparent',
      color: hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
      border: 'none',
      borderRadius: 'var(--radius-sm)',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'color 0.15s ease',
    }}
  >
    {children}
  </button>
);

export default CheckoutPage;
